import { z } from 'zod';
import { DateRangeSchema } from '../DateRange.js';
import { LedgerPolaritySchema, ReportRowSchema, RowGroupSchema, RowProvenanceSchema, } from './BusinessAnalysisSchemas.js';
import { CurrencyCodeSchema } from './MonetarySchemas.js';
// FieldDiff schema - generic field difference representation
export const FieldDiffSchema = (schema) => z.union([
    z.object({
        type: z.literal('left-only'),
        value: schema,
    }),
    z.object({
        type: z.literal('right-only'),
        value: schema,
    }),
    z.object({
        type: z.literal('both'),
        left: schema,
        right: schema,
    }),
]);
// Row property differences schema
// Updated for new schema: uses 'provenance' instead of 'sourceId'/'sourceType'
// Tracks all fields of MonetaryLedgerBalance: amount (value string), ledgerSide, and currency
export const RowPropertyDifferencesSchema = z.object({
    name: FieldDiffSchema(z.string()).optional(),
    amount: FieldDiffSchema(z.string()).optional(),
    ledgerSide: FieldDiffSchema(z.enum(['debit', 'credit'])).optional(),
    currency: FieldDiffSchema(CurrencyCodeSchema.nullable()).optional(),
    provenance: FieldDiffSchema(RowProvenanceSchema).optional(),
});
// Row diff schemas
export const RowOnlyInOneSchema = z.lazy(() => z.object({
    type: z.literal('only-in-one'),
    side: z.enum(['left', 'right']),
    row: ReportRowSchema,
}));
// RowGroupDiff schema
export const RowGroupDiffSchema = z.lazy(() => z.object({
    ledgerPolarity: z
        .object({
        type: z.literal('both'),
        left: LedgerPolaritySchema,
        right: LedgerPolaritySchema,
    })
        .optional(),
    rowsDiff: z.array(RowDiffSchema).optional(),
}));
// ChildrenDiff schema
export const ChildrenDiffSchema = z.lazy(() => z.union([
    z.object({
        side: z.enum(['left', 'right']),
        children: RowGroupSchema,
    }),
    RowGroupDiffSchema,
]));
export const RowInBothSchema = z.lazy(() => z.object({
    type: z.literal('in-both'),
    matchKey: z.object({ id: z.string() }), // Simplified: always match by id
    differences: RowPropertyDifferencesSchema.extend({
        childrenDiff: ChildrenDiffSchema.optional(),
    }),
}));
// Union type for RowDiff
export const RowDiffSchema = z.lazy(() => z.union([RowOnlyInOneSchema, RowInBothSchema]));
// Complete diff between two reports
// Updated for new schema: uses rowGroupDiff instead of rows array
export const ReportDiffSchema = z.object({
    metadata: z
        .object({
        entity: z
            .object({
            left: z.string(),
            right: z.string(),
        })
            .optional(),
        reportId: z
            .object({
            left: z.string(),
            right: z.string(),
        })
            .optional(),
        reportName: z
            .object({
            left: z.string(),
            right: z.string(),
        })
            .optional(),
        dateRange: z
            .object({
            left: DateRangeSchema,
            right: DateRangeSchema,
        })
            .optional(),
        generatedAt: z
            .object({
            left: z.string(),
            right: z.string(),
        })
            .optional(),
    })
        .optional(),
    rowGroupDiff: RowGroupDiffSchema.optional(),
});
// Diff visualization schemas
export const DiffStatusSchema = z.enum(['only-here', 'different', 'identical']);
export const DiffedRowGroupSchema = z.lazy(() => z.object({
    ledgerPolarity: z.object({
        left: LedgerPolaritySchema,
        right: LedgerPolaritySchema,
        status: z.enum(['identical', 'different']),
    }),
    rows: z.array(DiffedReportRowSchema),
}));
export const DiffedReportRowSchema = z.lazy(() => z.object({
    name: z.string(),
    left: z
        .object({
        row: ReportRowSchema,
        status: DiffStatusSchema,
    })
        .optional(),
    right: z
        .object({
        row: ReportRowSchema,
        status: DiffStatusSchema,
    })
        .optional(),
    propertyDifferences: RowPropertyDifferencesSchema.optional(),
    children: DiffedRowGroupSchema.optional(),
}));
export const VisualizationMetadataSchema = z.object({
    left: z.object({
        entity: z.string(),
        reportId: z.string(),
        reportName: z.string(),
        dateRange: DateRangeSchema,
        generatedAt: z.string(),
    }),
    right: z.object({
        entity: z.string(),
        reportId: z.string(),
        reportName: z.string(),
        dateRange: DateRangeSchema,
        generatedAt: z.string(),
    }),
});
export const ReportDiffForVisualizationSchema = z.object({
    metadata: VisualizationMetadataSchema,
    rowGroup: DiffedRowGroupSchema,
});
// Response schema for preview endpoint
export const ReportDiffPreviewResponseSchema = z.object({
    diffForVisualization: ReportDiffForVisualizationSchema,
    hasChanges: z.boolean().describe('Whether there are any differences in the report contents (excluding metadata)'),
});
//# sourceMappingURL=ReportDiffSchemas.js.map